import axios from "../../api/axiosInstance";
import { STATUS, extractErrorInfo } from "./sharedfunction";

/**
 * Fetch list of all apps
 * GET /app/fetch-app-list
 * @param {Object} params - Optional parameters
 * @param {string} params.projectId - Optional project ID to check connection status
 * @returns {Promise<Object>} Response with status, data (apps array), and message
 */
export async function fetchAppList({ projectId } = {}) {
  try {
    const params = {};
    if (projectId) {
      params.projectId = projectId;
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/app/fetch-app-list`,
      {
        params,
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || [],
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching app list:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Create a new app
 * POST /app/createapp
 * @param {Object} params - App creation parameters
 * @param {string} params.name - App name (1-10 characters)
 * @param {string} params.slug_name - Slug name (1-10 characters)
 * @param {string} [params.image_url] - Image URL (max 500 characters)
 * @param {string} [params.description] - Description (max 100 characters)
 * @returns {Promise<Object>} Response with status, data (app object), and message
 */
export async function createApp({ name, slug_name, image_url, description } = {}) {
  try {
    if (!name || name.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "App name is required",
      };
    }

    if (!slug_name || slug_name.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Slug name is required",
      };
    }

    const payload = {
      name: name.trim(),
      slug_name: slug_name.trim(),
      image_url: image_url?.trim() || null,
      description: description?.trim() || null,
    };

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/app/createapp`,
      payload,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error creating app:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Update an existing app
 * PUT /app/updateapp/:id
 * @param {Object} params - App update parameters
 * @param {string} params.id - App ID (UUID format)
 * @param {string} [params.name] - App name (1-10 characters)
 * @param {string} [params.slug_name] - Slug name (1-10 characters)
 * @param {string} [params.image_url] - Image URL (max 500 characters)
 * @param {string} [params.description] - Description (max 100 characters)
 * @returns {Promise<Object>} Response with status, data (app object), and message
 */
export async function updateApp({ id, name, slug_name, image_url, description } = {}) {
  try {
    if (!id || id.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "App ID is required",
      };
    }

    const payload = {};
    if (name !== undefined) payload.name = name?.trim() || null;
    if (slug_name !== undefined) payload.slug_name = slug_name?.trim() || null;
    if (image_url !== undefined) payload.image_url = image_url?.trim() || null;
    if (description !== undefined) payload.description = description?.trim() || null;

    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/app/updateapp/${id}`,
      payload,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error updating app:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Delete one or more apps
 * DELETE /app/deleteapp
 * @param {Object} params - Delete parameters
 * @param {Array<string>} params.appIds - Array of app IDs to delete
 * @returns {Promise<Object>} Response with status and data
 */
export async function deleteApps({ appIds } = {}) {
  try {
    if (!appIds || !Array.isArray(appIds) || appIds.length === 0) {
      return {
        status: STATUS.ERROR,
        error: "App IDs array is required",
      };
    }

    const response = await axios.delete(
      `${process.env.REACT_APP_API_URL}/app/deleteapp`,
      {
        data: { appIds },
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error deleting apps:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Get authorize URL for app connection
 * GET /:projectId/app-connection/authorize-url?app_id=xxx
 * @param {Object} params - Authorization parameters
 * @param {string} params.projectId - Project ID
 * @param {string} params.appId - App ID
 * @returns {Promise<Object>} Response with status, data (authorizeUrl), and message
 */
export async function getAuthorizeUrl({ projectId, appId } = {}) {
  try {
    if (!projectId || projectId.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!appId || appId.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "App ID is required",
      };
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/app-connection/authorize-url`,
      {
        params: { app_id: appId },
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error getting authorize URL:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Revoke app connection
 * POST /:projectId/app-connection/revoke-connection
 * @param {Object} params - Revoke parameters
 * @param {string} params.projectId - Project ID
 * @param {string} params.appId - App ID
 * @returns {Promise<Object>} Response with status and message
 */
export async function revokeAppConnection({ projectId, appId } = {}) {
  try {
    if (!projectId || projectId.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    if (!appId || appId.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "App ID is required",
      };
    }

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/${projectId}/app-connection/revoke-connection`,
      { app_id: appId },
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error revoking app connection:", error);
    return extractErrorInfo(error);
  }
}
